/**
 * Onboarding vocabulary — the single source of truth for onboarding state
 * names, orderings, floors, entry URLs, and labels.
 *
 * The names below are the app's vocabulary; their VALUES are computed from the
 * journey declarations in `./journey`, which is where a rung is added, moved or
 * retired. What stays here is the names, the pure helpers that read them, and
 * the access resolver.
 *
 * Pure and client-safe: no database, auth, or side-effectful imports. Server
 * modules build on this (`./transitions` for guarded writes, `./snapshot` for
 * the reconciled read, `./gates` for API gating).
 *
 * The two-level model:
 *   - Entity status (`builders.onboarding_status` / `subcontractors.onboarding_status`)
 *     tracks per-COMPANY progress. The enum arrays mirror the DB CHECK
 *     constraints exactly (chk_builder_onboarding_status /
 *     chk_sub_onboarding_status) — a mismatch is a bug.
 *   - User step (`users.onboarding_step`) tracks per-INDIVIDUAL progress
 *     (chk_user_onboarding_step). Legal agreements and CNDA are per-user gates
 *     that account-level progress never implies or skips — the journey's user
 *     lane, not a pair of names repeated wherever the rule bites.
 */

import * as journey from './journey/derived';
import type { BuilderStatus, OnboardingStep, SubStatus, UserStep } from './journey/derived';

export type { BuilderStatus, OnboardingStep, SubStatus, UserStep };

// ─── User steps ──────────────────────────────────────────────────────────────

/** Canonical per-user steps — the only values a writer may store. Historic
 *  spellings (`documents`, `phase1_review`, `phase2_review`) are accepted on
 *  read as aliases of the rung they name and never written. */
export const USER_STEPS = journey.USER_STEPS;

/** The writable steps with their journey labels, for the admin surfaces that
 *  offer a manual move. */
export const USER_STEP_OPTIONS = journey.USER_STEP_OPTIONS;
export type { UserStepOption } from './journey/derived';

/** Forward ordering of user steps (higher = further along). A legacy alias
 *  shares the ordinal of the rung it names, so a row stored under an old
 *  spelling compares identically to one stored under the new. */
export const STEP_ORDINAL: Readonly<Record<string, number>> = journey.STEP_ORDINAL;

/** The personal attestations — steps a user discharges themselves. An admin
 *  override may not target one: the row would say somebody attested with no
 *  acceptance behind it, which is not what the position means. */
export const USER_LANE_STEPS: ReadonlySet<string> = journey.USER_LANE_STEPS;

/** The writable steps that are company facts rather than personal
 *  attestations — what an admin override may target. Their complement is not a
 *  list kept anywhere: both fall out of the lane each rung declares. */
export const ENTITY_LANE_USER_STEPS = journey.ENTITY_LANE_USER_STEPS;

// ─── Entity statuses (mirror the DB CHECK constraints exactly) ───────────────

// These are the journey-progress (`onboarding_status`) values only — pure
// forward steps. Account-stop states (`suspended`, `archived`) are NOT here:
// they live on the orthogonal lifecycle `status` axis, mirroring how `archived`
// has always been lifecycle-only.
export const BUILDER_STATUSES = journey.BUILDER_STATUSES;

export const SUB_STATUSES = journey.SUB_STATUSES;

/** Human-facing labels for every entity onboarding status (builder + sub unions). */
export const STATUS_LABELS: Readonly<Record<string, string>> = journey.STATUS_LABELS;

// ─── State shape shared by the pure helpers ──────────────────────────────────

export interface OnboardingState {
  onboardingStep?: string | null;
  role?: string | null;
  builderId?: number | null;
  subcontractorId?: number | null;
  /** Owning builder's `builders.onboarding_status` (account-level progress). */
  builderOnboardingStatus?: string | null;
  /** Owning subcontractor's `subcontractors.onboarding_status` (account-level progress). */
  subcontractorOnboardingStatus?: string | null;
}

// ─── Who walks a journey ─────────────────────────────────────────────────────

/**
 * Whether an account with this role belongs to a company workspace — and so
 * whether it has a journey at all. Only the two customer roles do; a
 * specialist, a super_admin or any other role the platform grows is internal
 * staff, filed under no builder and no subcontractor.
 *
 * Stated once because both writers that can attach a person to a company read
 * it: the admin reassignment, and the invitation recorder (an invitation is
 * where a role comes FROM — registration takes it off the pending row).
 * Whatever is not a customer role is internal here, deliberately: a new role
 * must be added to a company workspace on purpose, never by having been
 * forgotten.
 *
 * Distinct from `isInternalRole` in `@/lib/notifications/policy`, which names
 * the two known staff roles positively to classify a notification AUDIENCE.
 * That question tolerates an unknown role; this one must not.
 */
export function isCompanyWorkspaceRole(role: string | null | undefined): boolean {
  return role === 'customer_admin' || role === 'customer_user';
}

/**
 * Which persona a company-workspace user is. A subcontractor link always
 * wins over a builder one — nothing in the schema links a row to both, but
 * every reader that has ever had to choose picks the same way, so the rule is
 * named once here instead of repeated as `subcontractor_id ? 'subcontractor'
 * : 'builder'` at each site.
 */
export function personaFor(
  subcontractorId: number | string | null | undefined,
): 'builder' | 'subcontractor' {
  return subcontractorId ? 'subcontractor' : 'builder';
}

// ─── Pure step helpers ───────────────────────────────────────────────────────

/** Returns true if current step is at or past minStep. Unknown or missing
 *  steps are treated as `cnda` (the conservative just-registered position). */
export function isStepAtLeast(current: string | null | undefined, min: OnboardingStep): boolean {
  const currentOrder = STEP_ORDINAL[current ?? 'cnda'] ?? STEP_ORDINAL['cnda'];
  const minOrder = STEP_ORDINAL[min] ?? 0;
  return currentOrder >= minOrder;
}

/** Returns true when `step`'s ordinal is strictly before `ref`'s — the exact
 *  complement of isStepAtLeast. Used to keep step advancement forward-only: a
 *  bulk step write may only touch users who are genuinely behind the target.
 *  Unknown or missing steps are treated as `cnda` (the conservative
 *  just-registered position). */
export function isStepBefore(step: string | null | undefined, ref: OnboardingStep): boolean {
  const stepOrder = STEP_ORDINAL[step ?? 'cnda'] ?? STEP_ORDINAL['cnda'];
  const refOrder = STEP_ORDINAL[ref] ?? 0;
  return stepOrder < refOrder;
}

/**
 * True when the user has discharged the personal attestations — the legal
 * agreements and the CNDA — and stands on a rung that is a company fact.
 *
 * The reference is the journey's FIRST entity-lane step rather than a step
 * named here, because the rung immediately after the CNDA is not a fixed one:
 * naming it by hand is how a helper comes to answer "has not signed" about a
 * user who signed and has simply moved on to the next company rung.
 */
export function hasCompletedCnda(step: string | null | undefined): boolean {
  return isStepAtLeast(step, journey.ENTITY_LANE_USER_STEPS[0]);
}

/**
 * Returns the URL the user should be on for portal/onboarding entry.
 * Super admin / specialist → admin dashboard. Builder → builder portal or
 * onboarding. Subcontractor → sub portal or onboarding.
 *
 * Callers must supply `builderId`/`subcontractorId` (null is fine): omitting
 * them routes every customer to the orphan dead-end. See
 * `requireOnboardingStepVerified`'s redirect construction for the full-input
 * pattern.
 */
export function getPortalEntryUrl(state: OnboardingState): string {
  const role = state.role;
  const step = state.onboardingStep || 'cnda';

  if (role === 'super_admin' || role === 'specialist') {
    return '/admin/dashboard';
  }

  // BO-4: Orphan user — has neither builderId nor subcontractorId.
  // This can happen if an admin unlinks or deletes the builder/sub while
  // the user still has an active session, or if a user registered without
  // a valid invitation. Route them to a clear dead-end page instead of
  // falling through to the builder portal (which would 403 on every API call).
  if (!state.builderId && !state.subcontractorId) {
    return '/auth/no-workspace';
  }

  const routing =
    personaFor(state.subcontractorId) === 'subcontractor'
      ? journey.SUBCONTRACTOR_ROUTING
      : journey.BUILDER_ROUTING;
  // A step the persona's journey does not name — an unknown value, or a rung
  // only the other persona walks (a sub has no phase 2) — lands on the
  // journey's home, which is what both switches' `default` arms did.
  return routing.byStep.get(step) ?? routing.home;
}

/**
 * Steps at which the user has cleared the guided, page-pinned onboarding phases
 * and may navigate the portal freely — the stages the journey declares `roam`.
 * These are exactly the steps whose portal entry is a dashboard; the
 * document-upload phases (`phase1_documents` / `phase2_documents`, and their
 * historic spellings) route to a specific page and stay pinned to it, so they
 * are absent.
 *
 * The portal layout consumes this instead of hard-coding a URL, so its
 * free-navigation gate can never drift from getPortalEntryUrl again (the prior
 * bug compared against a bare `/dashboard` that getPortalEntryUrl never returns,
 * pinning onboarded builders to their dashboard on every hard-load).
 *
 * Read off the declaration rather than compared against an ordinal, so a rung
 * that roams does not have to be preceded only by rungs that also do.
 */
const PORTAL_FREE_NAV_STEPS: ReadonlySet<string> = journey.PORTAL_FREE_NAV_STEPS;

export function allowsPortalFreeNavigation(step: string | null | undefined): boolean {
  return PORTAL_FREE_NAV_STEPS.has(step ?? '');
}

/** Portal pages reachable from the documents step ONWARD even while
 *  navigation is otherwise pinned to the journey's current page: the user's
 *  own profile and their entity's profile (the header dropdown links).
 *  Before documents — legal, CNDA, payment — the journey stays fully
 *  pinned. Product rule; the layout gate consumes this so the exemption
 *  can never drift from the engine. */
export const PORTAL_PROFILE_PATHS: readonly string[] = [
  '/portal/user-profile',
  '/portal/builder/profile',
  '/portal/subcontractor/profile',
];

export function allowsProfileNavigation(
  step: string | null | undefined,
  path: string | null | undefined,
): boolean {
  if (!path) return false;
  return isStepAtLeast(step, 'phase1_documents') && PORTAL_PROFILE_PATHS.includes(path);
}

/**
 * Is this path one the guided onboarding shell owns?
 *
 * Read off the journeys' own routes plus the re-consent interstitial, which
 * belongs to no rung — it is a block layered over wherever the user already is.
 * A sub-page counts as its rung's page, the way the Stripe return under the
 * payment rung has to render and process the checkout result.
 *
 * Answering from the declaration is what keeps a rung's page reachable the
 * moment the rung exists: the hand-kept list this replaced had drifted to
 * include a page no rung routes to, and would have locked a new rung's page out.
 */
export function isOnboardingPath(path: string | null | undefined): boolean {
  if (!path) return false;
  if (path === RECONSENT_URL) return true;
  return journey.ONBOARDING_ROUTES.some(
    (route) => path === route || path.startsWith(`${route}/`),
  );
}

// ─── Portal navigation ───────────────────────────────────────────────────────

export interface PortalNavItem {
  key: 'home' | 'compliance' | 'documents' | 'subcontractors' | 'profile';
  name: string;
  href: string;
  description: string;
}

const BUILDER_HOME: PortalNavItem = {
  key: 'home',
  name: 'Home',
  href: '/portal/builder/dashboard',
  description: 'Portal home and status',
};
const BUILDER_COMPLIANCE: PortalNavItem = {
  key: 'compliance',
  name: 'Compliance',
  href: '/portal/builder/documents',
  description: 'Your coverage and the documents behind it',
};
const BUILDER_DOCUMENTS_PINNED: PortalNavItem = {
  // Same destination as Compliance; during onboarding the page is the
  // document workspace, so the pinned item is labeled for what the user is
  // doing there right now.
  key: 'documents',
  name: 'Documents',
  href: '/portal/builder/documents',
  description: 'Upload your required documents',
};
const BUILDER_SUBCONTRACTORS: PortalNavItem = {
  key: 'subcontractors',
  name: 'Subcontractors',
  href: '/portal/builder/subcontractor-documents',
  description: 'Their documents and agreements',
};
const BUILDER_PROFILE: PortalNavItem = {
  key: 'profile',
  name: 'Company profile',
  href: '/portal/builder/profile',
  description: 'Details, billing, tax ID',
};

const SUB_HOME: PortalNavItem = {
  key: 'home',
  name: 'Home',
  href: '/portal/subcontractor/dashboard',
  description: 'Portal home and status',
};
const SUB_DOCUMENTS: PortalNavItem = {
  key: 'documents',
  name: 'Documents',
  href: '/portal/subcontractor/documents',
  description: 'Everything on file',
};
const SUB_PROFILE: PortalNavItem = {
  key: 'profile',
  name: 'Company profile',
  href: '/portal/subcontractor/profile',
  description: 'Details, trade, contacts',
};

/**
 * The sidebar items for a portal user — derived from the SAME state the
 * routing decisions use so navigation and `getPortalEntryUrl` can never
 * disagree (the old hardcoded nav once pointed at a different page than the
 * step the user was pinned to).
 *
 * Pinned steps (no free navigation) show exactly the current step's
 * destination. Free-navigation steps (see PORTAL_FREE_NAV_STEPS) show the full
 * nav; the builder's Subcontractors entry appears only once their journey
 * has reached Phase 2, since the page 403s before that.
 */
export function getPortalNav(state: {
  onboardingStep?: string | null;
  builderId?: number | null;
  subcontractorId?: number | null;
}): PortalNavItem[] {
  const isSub = !!state.subcontractorId;
  const step = state.onboardingStep ?? null;

  if (!allowsPortalFreeNavigation(step)) {
    if (isSub) return [SUB_DOCUMENTS];
    const entry = getPortalEntryUrl(state);
    return entry === BUILDER_SUBCONTRACTORS.href
      ? [BUILDER_SUBCONTRACTORS]
      : [BUILDER_DOCUMENTS_PINNED];
  }

  if (isSub) return [SUB_HOME, SUB_DOCUMENTS, SUB_PROFILE];

  const nav: PortalNavItem[] = [BUILDER_HOME, BUILDER_COMPLIANCE];
  if (isStepAtLeast(step, 'phase2_documents')) nav.push(BUILDER_SUBCONTRACTORS);
  nav.push(BUILDER_PROFILE);
  return nav;
}

// ─── Status → step floors ────────────────────────────────────────────────────

/**
 * Account-level user step implied by each entity `onboarding_status`.
 *
 * `users.onboarding_step` is per-user, but payment / documents / completion are
 * per-account facts (the org pays once; the document phases belong to the
 * builder, not each user). When a user is behind their account — e.g. someone
 * who joins a builder that already reached phase 2 — these maps say what step
 * the account's progress implies.
 *
 * Per-user gates (`legal_agreements`, `cnda`) are absent, and no longer by
 * being left out here: they are the journey's user lane, and a lane the account
 * cannot discharge has nothing to imply.
 */
export const BUILDER_STATUS_STEP_FLOOR: Readonly<Record<string, UserStep>> =
  journey.BUILDER_STATUS_STEP_FLOOR;

/** Subcontractors have no payment and no phase 2 — a single documents phase. */
export const SUBCONTRACTOR_STATUS_STEP_FLOOR: Readonly<Record<string, UserStep>> =
  journey.SUBCONTRACTOR_STATUS_STEP_FLOOR;

/**
 * Returns the step a user SHOULD be on, given their own stored step and their
 * account's `onboarding_status`. Fast-forwards a user who is behind the
 * account-level progress (the common cause of a linked/late-joining user being
 * stranded on an earlier phase) without ever moving anyone backward.
 *
 * Rules:
 *  - Personal attestations (the journey's user lane: legal, CNDA) are never
 *    skipped — a new user under an already-advanced account must still sign
 *    legal + CNDA themselves.
 *  - Admins/specialists and orphan users (no builder or subcontractor) are
 *    returned unchanged.
 *  - Forward-only: the result is the later of the user's own step and the step
 *    implied by the entity status (unknown/early statuses imply nothing).
 *
 * Pure and client-safe; the server computes the inputs (entity statuses) and
 * persists the result — see getOnboardingSnapshot / getReconciledOnboardingState.
 */
export function reconcileOnboardingStep(state: OnboardingState): string {
  const step = state.onboardingStep || 'cnda';

  if (state.role === 'super_admin' || state.role === 'specialist') return step;
  if (!state.builderId && !state.subcontractorId) return step;
  // An attestation is the user's own; no account-level progress lifts it.
  if (journey.USER_LANE_STEPS.has(step)) return step;

  const floor =
    personaFor(state.subcontractorId) === 'subcontractor'
      ? SUBCONTRACTOR_STATUS_STEP_FLOOR[state.subcontractorOnboardingStatus ?? '']
      : BUILDER_STATUS_STEP_FLOOR[state.builderOnboardingStatus ?? ''];
  if (!floor) return step;

  return isStepAtLeast(step, floor) ? step : floor;
}

// ─── Access resolver ─────────────────────────────────────────────────────────

/**
 * Interstitial a user lands on to re-accept a materially changed agreement,
 * shared by the resolver and the acceptance flow.
 */
export const RECONSENT_URL = '/onboarding/re-consent';

/** Dead-end shown when the owning workspace is stopped (suspended/archived). */
export const NO_WORKSPACE_URL = '/auth/no-workspace';

/** Why access is being routed/blocked. `step` is the ordinary "go to where you
 *  are in the journey" case (not a hard block). */
export type AccessReason = 'internal' | 'suspended' | 'reconsent' | 'step';

/** Who can clear a block: the account admin (suspend), the user themselves
 *  (re-consent / their own remaining steps), or nobody (internal bypass). */
export type WhoFixes = 'admin' | 'self' | null;

/** Facts the resolver needs — the routing inputs plus the two account-level
 *  blocks. Pure/client-safe: all fields are already-loaded scalars. */
export interface OnboardingAccessFacts extends OnboardingState {
  /** Owning entity's lifecycle status is a terminal stop (suspended/archived). */
  suspended: boolean;
  /** A gating agreement changed materially and this user has not re-accepted. */
  reconsentRequired: boolean;
}

export interface OnboardingAccessDecision {
  /** True only for a HARD block (suspended terminal or pending re-consent).
   *  The ordinary step case is not "blocked" — `fixUrl` is just where the user
   *  belongs in their flow. */
  blocked: boolean;
  reason: AccessReason;
  whoFixes: WhoFixes;
  /** Where to send the user: the block's remedy page, or their portal entry. */
  fixUrl: string;
}

/**
 * Decide portal/onboarding access for a user, in priority order:
 *   1. Internal roles (super_admin / specialist) bypass every block.
 *   2. A stopped workspace (lifecycle suspended/archived) is a terminal block
 *      only an admin can lift.
 *   3. A pending re-consent is a block the user clears themselves.
 *   4. Otherwise route to where the user is in their journey.
 *
 * Pure and client-safe — no database, auth, or side effects. The server
 * gathers the facts (see getOnboardingSnapshot) and both server gates and
 * client layouts consume the decision so the answer can never drift.
 */
export function resolveOnboardingAccess(facts: OnboardingAccessFacts): OnboardingAccessDecision {
  if (facts.role === 'super_admin' || facts.role === 'specialist') {
    return { blocked: false, reason: 'internal', whoFixes: null, fixUrl: getPortalEntryUrl(facts) };
  }

  if (facts.suspended) {
    return { blocked: true, reason: 'suspended', whoFixes: 'admin', fixUrl: NO_WORKSPACE_URL };
  }

  if (facts.reconsentRequired) {
    return { blocked: true, reason: 'reconsent', whoFixes: 'self', fixUrl: RECONSENT_URL };
  }

  return { blocked: false, reason: 'step', whoFixes: 'self', fixUrl: getPortalEntryUrl(facts) };
}
